import { useT } from "@transifex/react";
import { isTopLanguage, type CatalogLanguage } from "../../../helpers/BookCatalog";
import BookSelectionStepHeading from "./BookSelectionStepHeading";
import LanguageFlag from "./LanguageFlag";
import SelectionCard from "./SelectionCard";

interface LanguageStepProps {
    languages: Array<CatalogLanguage>;
    selectedSubjectId?: string;
    onPick: (subjectId: string) => void;
}

const LanguageStep = (props: LanguageStepProps) => {
    const t = useT();
    const t_title = t("Which language is your class learning?", { _tags: "CreateTest,SelectBook" });
    const t_commonLanguages = t("Frequently chosen", { _tags: "CreateTest,SelectBook" });
    const t_otherLanguages = t("More languages", { _tags: "CreateTest,SelectBook" });

    const renderCard = (language: CatalogLanguage) => {
        const count = language.books.length;
        const isSelected = language.subjectId === props.selectedSubjectId;

        return (
            <SelectionCard
                key={language.subjectId}
                isSelected={isSelected}
                onClick={() => props.onPick(language.subjectId)}
                data-testid="book-selection-language-card"
            >
                <span className="flex size-xl shrink-0 overflow-hidden rounded-circle ring-1 ring-transparent-black">
                    <LanguageFlag languageName={language.subjectName} />
                </span>
                <span className="flex min-w-no flex-col">
                    <span className="text-l font-medium text-p6-grey-dark">{language.subjectName}</span>
                    <span className="text-s text-p6-grey-mid">
                        {count === 1
                            ? t("{count} book", { _tags: "CreateTest,SelectBook", count })
                            : t("{count} books", { _tags: "CreateTest,SelectBook", count })}
                    </span>
                </span>
            </SelectionCard>
        );
    };

    const renderGroup = (title: string, languages: Array<CatalogLanguage>) =>
        languages.length > 0 && (
            <section className="flex flex-col gap-xs">
                <h4 className="m-no text-xs font-medium uppercase tracking-wider text-p6-grey-mid">{title}</h4>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(var(--size-selection-card),1fr))] gap-s">
                    {languages.map(renderCard)}
                </div>
            </section>
        );

    return (
        <div className="flex flex-col gap-s">
            <BookSelectionStepHeading title={t_title} />
            <div className="flex flex-col gap-m">
                {renderGroup(t_commonLanguages, props.languages.filter(isTopLanguage))}
                {renderGroup(
                    t_otherLanguages,
                    props.languages.filter((language) => !isTopLanguage(language))
                )}
            </div>
        </div>
    );
};

export default LanguageStep;
